<template>
  <v-card class="pa-6" flat>
    <div class="d-flex flex-column flex-sm-row justify-space-between align-start align-sm-center mb-4 gap-2">
      <div>
        <p class="grey--text text-body-2 mt-1 mb-0">
          لیست محصولات غیرفعال یا قطع اتصال‌شده از مارکت‌پلیس‌ها. برای فعال‌سازی مجدد، سوئیچ هر سطر را روشن کنید.
        </p>
      </div>
    </div>

    <!-- جدول نمایش لیستینگ‌ها -->
    <vue-table
      :data="listings"
      :headers="tableColumns"
      :loading.sync="loading"
      @change="onPagination"
    >
      <!-- فیلترهای جدول -->
      <template v-slot:filters="{ props, perPage }">
        <v-row no-gutters class="mb-4">
          <v-col class="pa-2" cols="12" lg="2" md="3">
            <v-select
              v-model="props.length"
              :items="perPage"
              label="تعداد در صفحه"
              dense outlined hide-details class="rounded-lg"
              @change="() => onPagination(props)"
            />
          </v-col>

          <v-col class="pa-2" cols="12" lg="4" md="4">
            <v-text-field
              v-model="props.search"
              label="جستجو (کد، عنوان، تنوع)..."
              prepend-inner-icon="mdi-magnify"
              dense outlined clearable hide-details class="rounded-lg"
              @keyup.enter="() => onPagination(props)"
              @click:clear="() => onPagination(props)"
            />
          </v-col>

          <v-col class="pa-2" cols="12" lg="3" md="3">
            <v-select
              v-model="props.marketplace"
              :items="marketplaceOptions"
              label="مارکت‌پلیس"
              dense outlined clearable hide-details class="rounded-lg"
              @change="() => onPagination(props)"
            />
          </v-col>
        </v-row>
      </template>

      <!-- اسلات ردیف -->
      <template v-slot:index="{ item }">
        {{ persian(item.index + 1 + ((listings.current_page || 1) - 1) * (listings.per_page || 10)) }}
      </template>

      <!-- اسلات عنوان محصول و شناسه مارکت -->
      <template v-slot:product_info="{ item }">
        <div class="d-flex align-center">
          <v-avatar v-if="item.metadata && item.metadata.image_src" size="40" rounded class="ml-2 grey lighten-4">
            <img :src="item.metadata.image_src" alt="product" />
          </v-avatar>
          <div>
            <div class="font-weight-bold grey--text text--darken-1">{{ item.product?.title || item.metadata?.title || '—' }}</div>
            <div class="grey--text text-caption">
              کد کالا در مارکت: <span class="font-weight-medium ltr-text">{{ item.external_id || '—' }}</span>
              <span v-if="item.external_variant_id" class="mr-2">
                (کد تنوع: <span class="ltr-text">{{ item.external_variant_id }}</span>)
              </span>
            </div>
          </div>
        </div>
      </template>

      <!-- اسلات مارکت‌پلیس -->
      <template v-slot:marketplace="{ item }">
        <v-chip
          small
          label
          :color="getMarketplaceBadge(item.marketplace).color"
          :text-color="getMarketplaceBadge(item.marketplace).textColor"
          class="font-weight-bold"
        >
          <v-icon left x-small>{{ getMarketplaceBadge(item.marketplace).icon }}</v-icon>
          {{ getMarketplaceBadge(item.marketplace).title }}
        </v-chip>
      </template>

      <!-- اسلات قیمت و استراتژی -->
      <template v-slot:pricing="{ item }">
        <div v-if="item.pricing_shipping || item.shipping_pricing">
          <div class="font-weight-bold">
            {{ persian(((item.pricing_shipping || item.shipping_pricing).price || 0).toLocaleString()) }} تومان
          </div>
        </div>
        <div v-else-if="item.metadata && item.metadata.price_sale">
          <span class="font-weight-medium">{{ persian(Number(item.metadata.price_sale).toLocaleString()) }} تومان</span>
        </div>
        <span v-else class="grey--text text-caption">تنظیم نشده</span>
      </template>

      <!-- اسلات موجودی در مارکت -->
      <template v-slot:stock="{ item }">
        <v-chip
          small
          :color="getStockCount(item) > 0 ? 'green lighten-5' : 'grey lighten-3'"
          :text-color="getStockCount(item) > 0 ? 'green darken-3' : 'grey darken-2'"
          class="font-weight-bold"
        >
          {{ persian(getStockCount(item)) }} عدد
        </v-chip>
      </template>

      <!-- اسلات وضعیت فعال/غیرفعال -->
      <template v-slot:is_active="{ item }">
        <v-switch
          v-model="item.active"
          color="success"
          hide-details
          dense
          :loading="item.loading"
          class="mt-0 pt-0 d-inline-block"
          @change="toggleListingStatus(item)"
        />
      </template>

      <!-- عملیات -->
      <template v-slot:action="{ item }">
        <action-buttons :data="item" :meta="metabutton(item)" />
      </template>
    </vue-table>

    <!-- مدال ویرایش -->
    <custom-popup
      v-model="showFormModal"
      :cons="{ title: 'ویرایش لیستینگ مارکت‌پلیس' }"
      max-width="750"
      hide-confirm
      @close="showFormModal = false"
    >
      <listing-form-modal
        v-if="showFormModal"
        :listing="selectedListing"
        :edit-mode="true"
        @saved="onListingSaved"
        @close="showFormModal = false"
      />
    </custom-popup>
  </v-card>
</template>

<script>
import ActionButtons from "@/view/components/adapters/qrList/ordersListButton.vue";
import ListingFormModal from "./ListingFormModal.vue";

export default {
  name: "MarketplaceListingsDeactive",
  components: { ActionButtons, ListingFormModal },
  data() {
    return {
      loading: false,
      listings: { data: [], total: 0, current_page: 1, per_page: 10 },
      showFormModal: false,
      selectedListing: null,

      marketplaceOptions: [
        { text: "همه مارکت‌ها", value: null },
        { text: "دیجی‌کالا", value: "digikala" },
        { text: "باسلام", value: "basalam" },
        { text: "اسنپ‌شاپ", value: "snappshop" },
      ],

      tableProps: {
        page: 1,
        length: 10,
        search: null,
        marketplace: null,
        active: 0, // فقط غیرفعال‌ها
      },
    };
  },

  computed: {
    tableColumns() {
      return [
        { value: "index", text: "ردیف", align: "center", sortable: false },
        { value: "product_info", text: "محصول / کد خارجی", align: "start", sortable: false },
        { value: "marketplace", text: "مارکت‌پلیس", align: "center", sortable: true },
        { value: "pricing", text: "قیمت جاری", align: "center", sortable: false },
        { value: "stock", text: "موجودی مارکت", align: "center", sortable: false },
        { value: "is_active", text: "وضعیت", align: "center", sortable: false },
        { value: "action", text: "عملیات", align: "center", sortable: false },
      ];
    },
  },

  mounted() {
    this.fetchListings();
  },

  methods: {
      openDigiManag(item) {
        const variantId = item.variant_id;
        const url = `https://seller.digikala.com/pwa/variant-management?filterSearchIn=search_term&filterSearchQuery=${variantId}&page=1`;
        
        window.open(url, '_blank');
      },
    openDigiShow(item) {
        const dkp = item.product_id;
        const url = `https://www.digikala.com/product/dkp-${dkp}/`;
        
        window.open(url, '_blank');
      },
    getStockCount(item) {
      if (item.pricing_shipping?.stock !== undefined) return item.pricing_shipping.stock;
      if (item.shipping_pricing?.stock !== undefined) return item.shipping_pricing.stock;
      if (item.metadata?.marketplace_seller_stock !== undefined) return item.metadata.marketplace_seller_stock;
      return 0;
    },

    metabutton(item) {
      return {
        buttons: [
         {
          name: "translateShowDigi",
          label: "نمایش در دیجی‌کالا",
          icon: "open-in-new", // تغییر از eye به open-in-new
          class: "btn-show rounded-pill",
          action: (row) => this.openDigiShow(row),
        },
        {
          name: "translateManagementDigi",
          label: "مدیریت در دیجی‌کالا",
          icon: "store-edit-outline",
          class: "btn-buy rounded-pill",
          action: (row) => this.openDigiManag(row),
        },

        //   {
        //     name: "delete",
        //     label: "حذف کامل",
        //     icon: "trash-can-outline",
        //     class: "btn-delete rounded-pill",
        //     action: (row) => this.deleteListing(row),
        //   },
        ],
      };
    },

    getMarketplaceBadge(marketplace) {
      const map = {
        digikala: { title: "دیجی‌کالا", color: "red lighten-5", textColor: "red darken-2", icon: "mdi-shopping" },
        basalam: { title: "باسلام", color: "orange lighten-5", textColor: "orange darken-3", icon: "mdi-storefront" },
        snappshop: { title: "اسنپ‌شاپ", color: "green lighten-5", textColor: "green darken-2", icon: "mdi-truck-fast" },
      };
      return map[marketplace] || { title: marketplace || "نامشخص", color: "grey lighten-3", textColor: "grey darken-2", icon: "mdi-web" };
    },

    async fetchListings() {
      this.loading = true;
      const params = {};
      Object.keys(this.tableProps).forEach((key) => {
        if (this.tableProps[key] !== null && this.tableProps[key] !== "") {
          params[key] = this.tableProps[key];
        }
      });

      try {
        const { data } = await this.$api.get("/v1/marketplace-listings", { params });
        this.listings = data.data || data;
      } catch (e) {
        this.$toast.error("خطا در دریافت لیست محصولات غیرفعال");
      } finally {
        this.loading = false;
      }
    },

    onPagination(e) {
      if (e && typeof e === "object" && !Array.isArray(e)) {
        this.tableProps = { ...this.tableProps, ...e, active: 0 };
        if ("search" in e || "marketplace" in e) {
          this.tableProps.page = 1;
        }
      }
      this.fetchListings();
    },

    async openEditModal(item) {
      this.loading = true;
      try {
        const { data } = await this.$api.get(`/v1/marketplace-listings/${item.id}`);
        this.selectedListing = data.data || data;
        this.showFormModal = true;
      } catch (e) {
        this.$toast.error("خطا در دریافت اطلاعات این محصول");
      } finally {
        this.loading = false;
      }
    },

    onListingSaved() {
      this.showFormModal = false;
      this.fetchListings();
    },

    async toggleListingStatus(item) {
      this.$set(item, "loading", true);
      try {
        await this.$api.post(`/v1/marketplace-listings/${item.id}/toggle-active`);
        this.$toast.success("محصول فعال شد و به لیست فعال‌ها انتقال یافت.");
        this.fetchListings(); // رفرش جدول
      } catch (e) {
        item.active = !item.active;
        this.$toast.error("خطا در فعال‌سازی محصول");
      } finally {
        this.$set(item, "loading", false);
      }
    },

    async deleteListing(item) {
      const confirm = await this.$modal.yesNo(`آیا از حذف دائم لیستینگ این محصول اطمینان دارید؟`);
      if (!confirm) return;

      this.loading = true;
      try {
        await this.$api.delete(`/v1/marketplace-listings/${item.id}`);
        this.$toast.success("لیستینگ حذف شد.");
        this.fetchListings();
      } catch (e) {
        this.$toast.error("خطا در حذف لیستینگ");
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
